/**
 * JCS Custom Product Page JavaScript
 * Handles gallery, variants, quantity, and add to cart functionality
 */

(function () {
  'use strict';

  class JCSProductPage {
    constructor(section) {
      this.section = section;
      this.productData = this.getProductData();

      if (!this.productData) return;

      this.currentVariant = this.productData.variants.find(v => v.available) || this.productData.variants[0];
      this.currentImageIndex = 0;

      this.initGallery();
      this.initVariants();
      this.initInventoryAlert();
      this.initQuantity();
      this.initAddToCart();
      this.initPurchaseOptions();
      this.initLightbox();
    }

    getProductData() {
      // The data-product-json script is a sibling of .jcs-product-section, not a child
      // Look in parent shopify-section wrapper first, then fall back to document
      let dataEl = this.section.parentElement?.querySelector('[data-product-json]');

      if (!dataEl) {
        // Fallback: try within the section itself
        dataEl = this.section.querySelector('[data-product-json]');
      }

      if (!dataEl) {
        console.warn('JCS: Product JSON data element not found');
        return null;
      }

      try {
        return JSON.parse(dataEl.textContent);
      } catch (e) {
        console.error('JCS: Failed to parse product data', e);
        return null;
      }
    }

    /* ========================================================================
       Gallery
       ======================================================================== */

    initGallery() {
      this.mainImage = this.section.querySelector('[data-main-image]');
      this.thumbnails = this.section.querySelectorAll('[data-thumbnail]');
      this.zoomLens = this.section.querySelector('[data-zoom-lens]');
      this.zoomResult = this.section.querySelector('[data-zoom-result]');
      this.zoomImage = this.section.querySelector('[data-zoom-image]');

      if (!this.mainImage || !this.thumbnails.length) return;

      this.thumbnails.forEach(thumb => {
        thumb.addEventListener('click', () => this.switchImage(thumb));
      });

      // Zoom functionality
      if (this.zoomLens && this.zoomResult) {
        const mainWrapper = this.section.querySelector('[data-main-image-wrapper]');

        mainWrapper.addEventListener('mouseenter', () => this.showZoom());
        mainWrapper.addEventListener('mouseleave', () => this.hideZoom());
        mainWrapper.addEventListener('mousemove', (e) => this.moveZoom(e));
      }
    }

    switchImage(thumb) {
      const index = parseInt(thumb.dataset.index, 10);
      const mediaId = thumb.dataset.mediaId;
      const media = this.productData.media.find(m => m.id == mediaId);

      if (!media) {
        console.warn('JCS: Media not found for id:', mediaId);
        return;
      }

      // Shopify stores image src in preview_image.src, fallback to src for compatibility
      const mediaSrc = media.preview_image?.src || media.src;

      if (!mediaSrc) {
        console.warn('JCS: No image source found for media:', media);
        return;
      }

      // Update main image - strip any existing query params and add width
      const baseSrc = mediaSrc.split('?')[0];
      this.mainImage.src = baseSrc + '?width=800';
      this.mainImage.srcset = `
        ${baseSrc}?width=400 400w,
        ${baseSrc}?width=600 600w,
        ${baseSrc}?width=800 800w,
        ${baseSrc}?width=1200 1200w
      `;

      // Update zoom image
      if (this.zoomImage) {
        this.zoomImage.src = baseSrc + '?width=1600';
      }

      // Update active state
      this.thumbnails.forEach(t => t.classList.remove('jcs-gallery__thumbnail--active'));
      thumb.classList.add('jcs-gallery__thumbnail--active');

      this.currentImageIndex = index;
    }

    showZoom() {
      if (window.innerWidth < 1024) return;
      this.zoomLens.hidden = false;
      this.zoomResult.hidden = false;
    }

    hideZoom() {
      this.zoomLens.hidden = true;
      this.zoomResult.hidden = true;
    }

    moveZoom(e) {
      if (this.zoomLens.hidden) return;

      const rect = this.mainImage.getBoundingClientRect();
      const lensSize = 150;

      let x = e.clientX - rect.left - lensSize / 2;
      let y = e.clientY - rect.top - lensSize / 2;

      // Constrain lens position
      x = Math.max(0, Math.min(x, rect.width - lensSize));
      y = Math.max(0, Math.min(y, rect.height - lensSize));

      this.zoomLens.style.left = x + 'px';
      this.zoomLens.style.top = y + 'px';

      // Calculate zoom ratio
      const zoomRatio = 1600 / rect.width;
      const zoomX = x * zoomRatio;
      const zoomY = y * zoomRatio;

      this.zoomImage.style.transform = `translate(-${zoomX}px, -${zoomY}px)`;
    }

    /* ========================================================================
       Lightbox
       ======================================================================== */

    initLightbox() {
      this.lightbox = this.section.querySelector('[data-lightbox]');
      if (!this.lightbox) return;

      const trigger = this.section.querySelector('[data-lightbox-trigger]');
      const closeButtons = this.lightbox.querySelectorAll('[data-lightbox-close]');
      const prevBtn = this.lightbox.querySelector('[data-lightbox-prev]');
      const nextBtn = this.lightbox.querySelector('[data-lightbox-next]');
      this.lightboxImage = this.lightbox.querySelector('[data-lightbox-image]');
      this.lightboxCounter = this.lightbox.querySelector('[data-lightbox-counter]');

      if (trigger) {
        trigger.addEventListener('click', () => this.openLightbox());
      }

      closeButtons.forEach(btn => {
        btn.addEventListener('click', () => this.closeLightbox());
      });

      if (prevBtn) prevBtn.addEventListener('click', () => this.lightboxNav(-1));
      if (nextBtn) nextBtn.addEventListener('click', () => this.lightboxNav(1));

      // Keyboard navigation
      document.addEventListener('keydown', (e) => {
        if (this.lightbox.hidden) return;
        if (e.key === 'Escape') this.closeLightbox();
        if (e.key === 'ArrowLeft') this.lightboxNav(-1);
        if (e.key === 'ArrowRight') this.lightboxNav(1);
      });
    }

    openLightbox() {
      this.lightbox.hidden = false;
      document.body.style.overflow = 'hidden';
      this.updateLightboxImage();
    }

    closeLightbox() {
      this.lightbox.hidden = true;
      document.body.style.overflow = '';
    }

    lightboxNav(direction) {
      const total = this.productData.media.length;
      this.currentImageIndex = (this.currentImageIndex + direction + total) % total;
      this.updateLightboxImage();
    }

    updateLightboxImage() {
      const media = this.productData.media[this.currentImageIndex];
      if (!media) return;

      this.lightboxImage.src = media.src.replace(/\?.*/, '') + '?width=1200';
      this.lightboxImage.alt = media.alt || this.productData.title;

      if (this.lightboxCounter) {
        this.lightboxCounter.textContent = `${this.currentImageIndex + 1} / ${this.productData.media.length}`;
      }
    }

    /* ========================================================================
       Variants
       ======================================================================== */

    initVariants() {
      this.variantSelects = this.section.querySelectorAll('[data-option-index]');
      this.variantIdInput = this.section.querySelector('[data-variant-id]');
      this.priceElement = this.section.querySelector('[data-product-price]');
      this.addToCartButton = this.section.querySelector('[data-add-to-cart]');
      this.addToCartText = this.section.querySelector('[data-add-to-cart-text]');

      if (!this.variantSelects.length) return;

      this.variantSelects.forEach(select => {
        select.addEventListener('change', () => this.onVariantChange());
      });
    }

    onVariantChange() {
      // Build options array using data-option-index so hidden/skipped
      // options (e.g. single-value Color) don't shift positions.
      const selectedOptions = [];
      this.variantSelects.forEach(select => {
        const idx = parseInt(select.dataset.optionIndex, 10);
        selectedOptions[idx] = select.value;
      });

      // Find matching variant — only compare options that have a
      // rendered selector (skipped options are undefined in the array).
      const variant = this.productData.variants.find(v => {
        return v.options.every((opt, i) => {
          if (selectedOptions[i] === undefined) return true;
          return opt === selectedOptions[i];
        });
      });

      if (!variant) return;

      this.currentVariant = variant;
      this.updateVariantUI(variant);
    }

    updateVariantUI(variant) {
      // Update hidden input
      if (this.variantIdInput) {
        this.variantIdInput.value = variant.id;
      }

      // Update price
      if (this.priceElement) {
        const priceHtml = this.formatMoney(variant.price);
        let html = `<span class="jcs-price jcs-price--current">${priceHtml}</span>`;

        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          const compareHtml = this.formatMoney(variant.compare_at_price);
          const savings = this.formatMoney(variant.compare_at_price - variant.price);
          html = `
            <span class="jcs-price jcs-price--compare">${compareHtml}</span>
            ${html}
            <span class="jcs-price__savings">Save ${savings}</span>
          `;
        }

        this.priceElement.innerHTML = html;
      }

      // Update button state
      if (this.addToCartButton && this.addToCartText) {
        if (variant.available) {
          this.addToCartButton.disabled = false;
          this.addToCartText.textContent = this.addToCartButton.dataset.addText || 'ADD TO CART';
        } else {
          this.addToCartButton.disabled = true;
          this.addToCartText.textContent = this.addToCartButton.dataset.soldText || 'SOLD OUT';
        }
      }

      // Update URL
      if (history.replaceState) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        history.replaceState({}, '', url.toString());
      }

      // Switch to variant image if exists
      if (variant.featured_media) {
        const thumb = this.section.querySelector(`[data-media-id="${variant.featured_media.id}"]`);
        if (thumb) this.switchImage(thumb);
      }

      // Update low-inventory alert
      this.updateInventoryAlert(variant);
    }

    /* ========================================================================
       Low-Inventory Alert
       ======================================================================== */

    initInventoryAlert() {
      this.itemsLeftEl = this.section.querySelector('[data-items-left]');

      // Like data-product-json, the inventory map is a sibling of
      // .jcs-product-section inside the shopify-section wrapper
      let invEl = this.section.parentElement?.querySelector('[data-variant-inventory]');
      if (!invEl) {
        invEl = this.section.querySelector('[data-variant-inventory]');
      }

      this.variantInventory = null;

      if (!this.itemsLeftEl || !invEl) return;

      try {
        this.variantInventory = JSON.parse(invEl.textContent);
      } catch (e) {
        console.error('JCS: Failed to parse variant inventory data', e);
      }
    }

    updateInventoryAlert(variant) {
      if (!this.itemsLeftEl || !this.variantInventory) return;

      const settings = window.PXUTheme?.theme_settings || {};
      const translation = window.PXUTheme?.translation || {};
      const threshold = parseInt(settings.inventory_threshold, 10) || 0;
      const inventory = this.variantInventory.find(v => v.id === variant.id);

      const showAlert =
        settings.display_inventory_left &&
        inventory &&
        inventory.inventory_management &&
        inventory.inventory_quantity > 0 &&
        inventory.inventory_quantity <= threshold;

      if (showAlert) {
        const text = inventory.inventory_quantity === 1
          ? (translation.product_count_one || 'item left')
          : (translation.product_count_other || 'items left');
        this.itemsLeftEl.textContent = `${inventory.inventory_quantity} ${text}`;
      } else {
        this.itemsLeftEl.textContent = '';
      }
    }

    formatMoney(cents) {
      const amount = (cents / 100).toFixed(2);
      return '$' + amount;
    }

    /* ========================================================================
       Quantity
       ======================================================================== */

    initQuantity() {
      const quantityWrapper = this.section.querySelector('.jcs-quantity');
      if (!quantityWrapper) return;

      this.quantityInput = quantityWrapper.querySelector('.jcs-quantity__input');
      const minusBtn = quantityWrapper.querySelector('.jcs-quantity__btn--minus');
      const plusBtn = quantityWrapper.querySelector('.jcs-quantity__btn--plus');

      if (minusBtn) {
        minusBtn.addEventListener('click', () => this.updateQuantity(-1));
      }

      if (plusBtn) {
        plusBtn.addEventListener('click', () => this.updateQuantity(1));
      }

      if (this.quantityInput) {
        this.quantityInput.addEventListener('change', () => this.validateQuantity());
      }
    }

    updateQuantity(change) {
      if (!this.quantityInput) return;

      const min = parseInt(this.quantityInput.min, 10) || 1;
      const max = parseInt(this.quantityInput.max, 10) || 99;
      let value = parseInt(this.quantityInput.value, 10) || 1;

      value = Math.max(min, Math.min(max, value + change));
      this.quantityInput.value = value;
    }

    validateQuantity() {
      if (!this.quantityInput) return;

      const min = parseInt(this.quantityInput.min, 10) || 1;
      const max = parseInt(this.quantityInput.max, 10) || 99;
      let value = parseInt(this.quantityInput.value, 10) || 1;

      this.quantityInput.value = Math.max(min, Math.min(max, value));
    }

    /* ========================================================================
       Add to Cart
       ======================================================================== */

    initAddToCart() {
      // The theme's ajax-submit class handles add-to-cart functionality
      // including opening the cart drawer/mini-cart.
      // Quantity input is now inside the form, so it submits automatically.
      // No additional handling needed.
    }

    /* ========================================================================
       Purchase Options
       ======================================================================== */

    initPurchaseOptions() {
      const options = this.section.querySelectorAll('[data-purchase-option]');
      if (!options.length) return;

      options.forEach(option => {
        const radio = option.querySelector('.jcs-purchase-option__radio');
        if (radio) {
          radio.addEventListener('change', () => {
            options.forEach(o => o.classList.remove('jcs-purchase-option--active'));
            option.classList.add('jcs-purchase-option--active');
          });
        }
      });
    }
  }

  // Initialize on DOM ready
  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('.jcs-product-section').forEach(section => {
      new JCSProductPage(section);
    });
  });

  // Reinitialize on Shopify section load (Theme Editor)
  document.addEventListener('shopify:section:load', (e) => {
    if (e.target.querySelector('.jcs-product-section')) {
      new JCSProductPage(e.target.querySelector('.jcs-product-section'));
    }
  });

})();